/**
 * Invite people to space with Member role.
 */

import React, { Fragment } from "react";
import {
  Box,
  Text,
  Notice,
  Textarea,
  Space,
  Kit,
  TokenInputToken,
} from "@thenounproject/lingo-core";
import EmailInput from "../../forms/EmailInput";
import ModalBody from "../../../../app/_components/ModalBody";
import ModalFooter from "../../../../app/_components/ModalFooter";
import SpaceFullMessage from "./SpaceFullMessage";

type Props = {
  space: Space;
  emails: TokenInputToken[];
  addEmails: (email: TokenInputToken[]) => void;
  removeEmail: (index: number) => void;
  note: string;
  onNoteChanged: (note: string) => void;
  error: string;
  processing: boolean;
  sendInvitations: () => void;
  tooManyInvitations: boolean;
  kits: Kit[];
  selectedKitRoles: Record<string, string[]>;
  selectKitRoles: (kitIds: string[], role: string) => void;
  setDefaultKitRole: (kitIds: string[], role: string) => void;
  billingNotice?: string;
};

const InviteMembers = ({
  space,
  emails,
  addEmails,
  removeEmail,
  note,
  onNoteChanged,
  error,
  processing,
  sendInvitations,
  tooManyInvitations,
  billingNotice,
}: Props) => {
  const hasInvalidEmails = emails.some(i => i.isValid === false);

  function renderLimitWarning() {
    // Show a warning if we input more invitations than we have room for
    // If the space is completely full, a notice is shown in InviteSpaceMembers
    // and we won't be able to get to this view.
    return tooManyInvitations ? (
      <SpaceFullMessage spaceId={space.id} permissions={space.access.permissions} />
    ) : null;
  }

  return (
    <Fragment>
      <ModalBody>
        <Box>
          {billingNotice && <Notice message={billingNotice} noticeStyle="warning" mb="m" />}
          <Text font="ui.regularBold">Invite Content Managers</Text>
          <Text>
            Content managers can access all Kits. They can only edit Kits they are allowed to by an
            Admin or Owner.
          </Text>
        </Box>
        {renderLimitWarning()}

        {/* Email input */}
        <Text as="label" mt="xl" mb="xs">
          Emails
        </Text>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label>
          <EmailInput
            onAddToken={addEmails}
            onRemoveToken={removeEmail}
            items={emails}
            inputProps={{ style: { maxWidth: "580px" } }}
          />
        </label>

        {/* Email validation error */}
        {hasInvalidEmails ? (
          <Text ml="xs" mt="xs" font="ui.small" color="error">
            Items in red will not be submitted.
          </Text>
        ) : null}
        {/* Note */}
        <Textarea
          mt="l"
          value={note}
          label="Note (optional)"
          placeholder="Add a personal note"
          onChange={e => onNoteChanged(e.target.value)}
        />

        {error ? <Notice noticeStyle="error" message={error} /> : null}
      </ModalBody>
      <ModalFooter
        primary={{
          text: processing ? "Sending..." : "Send",
          disabled: emails.length === 0 || processing,
          onClick: sendInvitations,
        }}
      />
    </Fragment>
  );
};

export default InviteMembers;
